import { useState } from "react";
import { Plus, X } from "lucide-react";
import { useTranslation } from "@ncp/i18n";
import { Badge, Button, Field, Input, NativeSelect, Textarea } from "@ncp/ui";
import type { FormBody, FormPurpose } from "./api";
import { useFormUsedBy } from "./api";
import { useSurfaceLinks } from "../../lib/links";

interface Props {
  body: FormBody;
  formKey: string;
  onChange: (next: FormBody, opts?: { transient?: boolean }) => void;
  readOnly?: boolean;
}

const PURPOSES: FormPurpose[] = ["Intake", "Rfi", "Objection", "Internal"];

/** Form metadata: bilingual name and description, purpose, tags, data-contract bindings and which services use it. */
export function FormSettings({ body, formKey, onChange, readOnly }: Props) {
  const { t } = useTranslation();
  const { openInBackoffice } = useSurfaceLinks();
  const usedBy = useFormUsedBy(formKey);
  const [tag, setTag] = useState("");
  const [bindPath, setBindPath] = useState("");
  const [bindKey, setBindKey] = useState("");
  const patch = (p: Partial<FormBody>, transient = true) => onChange({ ...body, ...p }, { transient });

  return (
    <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_320px]">
      <div className="space-y-4">
        <section className="rounded-xl border border-sand-200 bg-white/80 p-4">
          <h3 className="mb-3 text-sm font-semibold">{t("admin:forms.settings.identity")}</h3>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={t("admin:common.name")} required><Input value={body.name} readOnly={readOnly} onChange={(e) => patch({ name: e.target.value })} /></Field>
            <Field label={t("admin:common.nameAr")}><Input value={body.nameAr ?? ""} readOnly={readOnly} dir="rtl" onChange={(e) => patch({ nameAr: e.target.value || null })} /></Field>
            <Field label={t("admin:common.description")}><Textarea value={body.description ?? ""} readOnly={readOnly} className="min-h-[72px]" onChange={(e) => patch({ description: e.target.value || null })} /></Field>
            <Field label={t("admin:common.descriptionAr")}><Textarea value={body.descriptionAr ?? ""} readOnly={readOnly} dir="rtl" className="min-h-[72px]" onChange={(e) => patch({ descriptionAr: e.target.value || null })} /></Field>
            <Field label={t("admin:common.key")} hint={t("admin:forms.settings.keyHint")}><Input value={formKey} readOnly dir="ltr" className="font-mono" /></Field>
            <Field label={t("admin:forms.settings.purpose")} hint={t("admin:forms.settings.purposeHint")}>
              <NativeSelect value={body.purpose} disabled={readOnly} onChange={(e) => patch({ purpose: e.target.value as FormPurpose }, false)}>{PURPOSES.map((p) => <option key={p} value={p}>{t(`admin:forms.purpose.${p}`)}</option>)}</NativeSelect>
            </Field>
          </div>
          <Field label={t("admin:forms.settings.tags")} hint={t("admin:forms.settings.tagsHint")} className="mt-3">
            <div className="flex flex-wrap items-center gap-1.5">
              {body.tags.map((tg) => <Badge key={tg} variant="outline" size="sm" className="gap-1">{tg}{!readOnly ? <button type="button" onClick={() => patch({ tags: body.tags.filter((x) => x !== tg) }, false)} aria-label={t("admin:common.delete")}><X className="size-3" /></button> : null}</Badge>)}
              {!readOnly ? <form className="flex items-center gap-1" onSubmit={(e) => { e.preventDefault(); const v = tag.trim().toLowerCase(); if (v && !body.tags.includes(v)) patch({ tags: [...body.tags, v] }, false); setTag(""); }}><Input value={tag} onChange={(e) => setTag(e.target.value)} className="h-7 w-32 text-xs" placeholder={t("admin:forms.settings.addTag")} /><Button size="sm" variant="ghost" type="submit"><Plus /></Button></form> : null}
            </div>
          </Field>
        </section>

        <section className="rounded-xl border border-sand-200 bg-white/80 p-4">
          <h3 className="mb-1 text-sm font-semibold">{t("admin:forms.settings.bindings")}</h3>
          <p className="mb-3 text-xs text-muted-foreground">{t("admin:forms.settings.bindingsHint")}</p>
          <table className="w-full text-xs">
            <thead><tr className="text-start text-muted-foreground"><th className="pb-1 text-start font-medium">{t("admin:forms.settings.formPath")}</th><th className="pb-1 text-start font-medium">{t("admin:forms.settings.contractKey")}</th><th /></tr></thead>
            <tbody>
              {Object.entries(body.bindings).map(([path, key]) => (
                <tr key={path} className="border-t border-sand-100">
                  <td className="py-1 font-mono" dir="ltr">{path}</td>
                  <td className="py-1"><Input value={key} readOnly={readOnly} dir="ltr" className="h-7 font-mono text-xs" onChange={(e) => patch({ bindings: { ...body.bindings, [path]: e.target.value } })} /></td>
                  <td className="py-1 text-end">{!readOnly ? <Button size="sm" variant="ghost" onClick={() => { const b = { ...body.bindings }; delete b[path]; patch({ bindings: b }, false); }}><X /></Button> : null}</td>
                </tr>
              ))}
              {Object.keys(body.bindings).length === 0 ? <tr><td colSpan={3} className="py-3 text-center text-muted-foreground">{t("admin:forms.settings.noBindings")}</td></tr> : null}
            </tbody>
          </table>
          {!readOnly ? (
            <form className="mt-2 flex items-center gap-2" onSubmit={(e) => { e.preventDefault(); if (bindPath.trim() && bindKey.trim()) { patch({ bindings: { ...body.bindings, [bindPath.trim()]: bindKey.trim() } }, false); setBindPath(""); setBindKey(""); } }}>
              <Input value={bindPath} onChange={(e) => setBindPath(e.target.value)} dir="ltr" className="h-8 font-mono text-xs" placeholder="parties[0].name" />
              <span className="text-muted-foreground">→</span>
              <Input value={bindKey} onChange={(e) => setBindKey(e.target.value)} dir="ltr" className="h-8 font-mono text-xs" placeholder="acquirer.name" />
              <Button size="sm" variant="outline" type="submit"><Plus /> {t("admin:forms.settings.addBinding")}</Button>
            </form>
          ) : null}
        </section>
      </div>

      <aside className="space-y-3 xl:sticky xl:top-20 xl:self-start">
        <div className="rounded-xl border border-sand-200 bg-white/80 p-3">
          <h3 className="mb-1 text-sm font-semibold">{t("admin:forms.settings.usedBy")}</h3>
          <p className="mb-2 text-[11px] text-muted-foreground">{t("admin:forms.settings.usedByHint")}</p>
          {(usedBy.data ?? []).length === 0 ? <p className="text-xs text-muted-foreground">{t("admin:forms.settings.notUsed")}</p> : (
            <ul className="space-y-1 text-xs">
              {(usedBy.data ?? []).map((s) => <li key={`${s.key}-${s.version}`} className="flex items-center justify-between gap-2 rounded-md bg-sand-50 px-2 py-1"><span className="truncate">{s.name}</span><span className="flex items-center gap-1"><Badge variant="outline" size="sm" className="font-mono">{s.key}</Badge><Badge variant={s.status === "Published" ? "success" : "info"} size="sm">v{s.version}</Badge></span></li>)}
            </ul>
          )}
        </div>
        <div className="rounded-xl border border-sand-200 bg-white/80 p-3 text-xs text-muted-foreground">
          <h3 className="mb-1 text-sm font-semibold text-foreground">{t("admin:forms.settings.whereRendered")}</h3>
          <p>{t("admin:forms.settings.whereRenderedHint")}</p>
          <Button size="sm" variant="ghost" className="mt-2 px-0" onClick={() => openInBackoffice("/cases")}>{t("admin:forms.settings.openBackoffice")}</Button>
        </div>
      </aside>
    </div>
  );
}
